import React, { useState, useEffect, useRef } from 'react';
import { transactionService } from '../services/transactionService';
import type { Transaction } from '../types/api';

interface TransactionMonitorProps {
  transactionId: string | null;
  onTransactionUpdate?: (transaction: Transaction) => void;
  autoRefresh?: boolean;
  // Optional currency override (e.g., derived from selected profile in parent)
  currency?: string;
}

const TransactionMonitor: React.FC<TransactionMonitorProps> = ({
  transactionId,
  onTransactionUpdate,
  autoRefresh = true,
  currency
}) => {
  const [transaction, setTransaction] = useState<Transaction | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [isPolling, setIsPolling] = useState(autoRefresh);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch transaction details
  const fetchTransaction = async () => {
    if (!transactionId) return;

    try {
      setIsLoading(true);
      setError('');

      const response = await transactionService.getTransaction(transactionId);

      if (response.success && response.data) {
        setTransaction(response.data);
        setLastUpdate(new Date());
        
        // Notify parent component
        if (onTransactionUpdate) {
          onTransactionUpdate(response.data);
        }
      } else {
        setError(response.message || 'Failed to fetch transaction');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to fetch transaction');
    } finally {
      setIsLoading(false);
    }
  };

  // Start/stop polling
  useEffect(() => {
    if (transactionId && isPolling) {
      // Initial fetch
      fetchTransaction();

      // Set up polling interval (every 1 second for faster error detection)
      intervalRef.current = setInterval(() => {
        fetchTransaction();
      }, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [transactionId, isPolling]);

  // Stop polling when transaction is completed or failed
  useEffect(() => {
    if (transaction && ['done', 'completed', 'rejected', 'failed'].includes(transaction.statusTxt)) {
      setIsPolling(false);
    }
  }, [transaction]);

  const togglePolling = () => {
    setIsPolling(!isPolling);
  };

  const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'done':
      case 'completed':
        return 'text-green-600 bg-green-50';
      case 'processing':
        return 'text-blue-600 bg-blue-50';
      case 'waiting':
      case 'pendingpayment':
        return 'text-yellow-600 bg-yellow-50';
      case 'pending':
        return 'text-gray-600 bg-gray-50';
      case 'rejected':
      case 'failed':
        return 'text-red-600 bg-red-50';
      case 'userlogin':
        return 'text-blue-600 bg-blue-50';
      case 'selectaccount':
      case 'account_selection_required':
      case 'SELECTACCOUNT':
        return 'text-purple-600 bg-purple-50';
      case 'waitapproval':
        return 'text-orange-600 bg-orange-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'done':
      case 'completed':
        return '✅';
      case 'processing':
        return '⚙️';
      case 'waiting':
      case 'pendingpayment':
        return '⏳';
      case 'pending':
        return '🔄';
      case 'rejected':
      case 'failed':
        return '❌';
      case 'userlogin':
        return '🔐';
      case 'selectaccount':
      case 'account_selection_required':
      case 'SELECTACCOUNT':
        return '🏦';
      case 'waitapproval':
        return '⏰';
      default:
        return '❓';
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  const formatCurrency = (amount: number): string => {
    // Determine currency: prefer prop override, then derive from populated profile, else default to MYR
    let code = currency;

    if (!code && transaction && typeof transaction.profileId === 'object') {
      const profile: any = transaction.profileId as any;
      const bank = (profile?.accbank || '').toLowerCase();
      if (bank.includes('kbankpromptpay')) {
        code = 'THB';
      } else if (profile?.currency) {
        code = String(profile.currency).toUpperCase();
      }
    }

    if (!code) code = 'MYR';

    // Use shared formatter for consistency across the app
    return transactionService.formatAmount(amount, code);
  };

  if (!transactionId) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>No transaction to monitor</p>
        <p className="text-sm mt-2">Create a payment to see transaction details</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">Transaction Monitor</h3>
        <div className="flex items-center space-x-2">
          {isPolling && (
            <span className="text-xs text-green-600 font-medium flex items-center">
              <span className="animate-pulse mr-1">🔄</span>
              Auto-refresh (3s)
            </span>
          )}
          <button
            onClick={togglePolling}
            className={`px-3 py-1 text-xs rounded-md font-medium ${
              isPolling 
                ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {isPolling ? '⏸️ Pause' : '▶️ Start'}
          </button>
          <button
            onClick={fetchTransaction}
            disabled={isLoading}
            className="px-3 py-1 text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-md font-medium disabled:opacity-50"
          >
            {isLoading ? '⏳' : '🔄'}
          </button>
        </div>
      </div>

      {/* Last Update */}
      {lastUpdate && (
        <div className="text-xs text-gray-500 text-center">
          Last updated: {lastUpdate.toLocaleTimeString()}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Transaction Details */}
      {transaction && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
          {/* Status */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-lg">{getStatusIcon(transaction.statusTxt)}</span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(transaction.statusTxt)}`}>
                {transaction.statusTxt.toUpperCase()}
              </span>
            </div>
            <div className="text-sm text-gray-500">
              ID: {transaction._id}
            </div>
          </div>

          {/* Transaction Info */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Amount:</span>
              <div className="font-semibold text-lg">{formatCurrency(transaction.amount)}</div>
            </div>
            <div>
              <span className="text-gray-500">Type:</span>
              <div className="font-medium">
                {transaction.transaction === 'receiving' ? '📥 Receiving' : '📤 Transfer'}
              </div>
            </div>
            <div>
              <span className="text-gray-500">Created:</span>
              <div>{formatDate(transaction.createdAt)}</div>
            </div>
            <div>
              <span className="text-gray-500">Updated:</span>
              <div>{formatDate(transaction.updatedAt)}</div>
            </div>
          </div>

          {/* Client ID */}
          {transaction.clientId && (
            <div className="text-sm">
              <span className="text-gray-500">Client ID:</span>
              <div className="font-mono text-xs bg-gray-100 p-2 rounded mt-1">
                {transaction.clientId}
              </div>
            </div>
          )}

          {/* Remark */}
          {transaction.remark && (
            <div className="text-sm">
              <span className="text-gray-500">Remark:</span>
              <div className="mt-1">{transaction.remark}</div>
            </div>
          )}

          {/* Error Message */}
          {transaction.errorMessage && ['failed', 'rejected'].includes(transaction.statusTxt) && (
            <div className="text-sm">
              <span className="text-red-600 font-medium">Error:</span>
              <div className="mt-1 p-3 bg-red-50 border border-red-200 rounded-md text-red-700">
                {transaction.errorMessage}
              </div>
            </div>
          )}

          {/* QR Content */}
          {transaction.qrContent && (
            <div className="text-sm">
              <span className="text-gray-500">QR Content:</span>
              <div className="font-mono text-xs bg-gray-100 p-2 rounded mt-1 break-all">
                {transaction.qrContent.length > 100 
                  ? `${transaction.qrContent.substring(0, 100)}...` 
                  : transaction.qrContent
                }
              </div>
              <button
                onClick={() => navigator.clipboard.writeText(transaction.qrContent || '')}
                className="mt-2 text-xs text-blue-600 hover:text-blue-800"
              >
                📋 Copy QR Content
              </button>
            </div>
          )}

          {/* Timeout Info */}
          {transaction.timeoutSeconds && (
            <div className="text-sm">
              <span className="text-gray-500">Timeout:</span>
              <div>{transaction.timeoutSeconds} seconds</div>
              {transaction.timeoutAt && (
                <div className="text-xs text-gray-400">
                  Expires at: {formatDate(transaction.timeoutAt)}
                </div>
              )}
            </div>
          )}

          {/* Logs */}
          {transaction.logs && transaction.logs.length > 0 && (
            <div className="text-sm">
              <span className="text-gray-500">Activity Log:</span>
              <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                {transaction.logs.map((log, index) => (
                  <div key={index} className="text-xs bg-gray-50 p-2 rounded">
                    <div className="font-medium text-gray-700">
                      {formatDate(log.timestamp)}
                    </div>
                    <div className="text-gray-600">{log.message}</div>
                    {log.remark && (
                      <div className="text-gray-500 italic">({log.remark})</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TransactionMonitor;
